import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useKarmaProfileData } from "./useKarmaProfileData";
import { useKarmaActivityLog } from "./useKarmaActivityLog";
import { useKarmaClaimAvailability } from "./useKarmaClaimAvailability";
import { useKarmaLeaderboardData } from "./useKarmaLeaderboardData";

export const useKarmaData = () => {
  const { user } = useAuth();
  
  const {
    karmaPoints,
    setKarmaPoints,
    currentLevel,
    setCurrentLevel,
    loadingProfile,
    fetchUserKarmaProfile,
  } = useKarmaProfileData();

  const {
    activities,
    setActivities,
    loadingActivities,
    fetchKarmaActivities,
  } = useKarmaActivityLog();

  const {
    availablePoints,
    setAvailablePoints,
    loadingAvailability,
    checkAvailableKarmaPoints,
  } = useKarmaClaimAvailability();

  const {
    leaderboard,
    loadingLeaderboard,
    fetchKarmaLeaderboard,
  } = useKarmaLeaderboardData();
  
  const [loading, setLoading] = useState(true); // Overall loading for initial user data
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());

  const loadInitialData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Fetch primary user data in parallel where possible or sequence if dependent
    await Promise.all([
      fetchUserKarmaProfile(),
      fetchKarmaActivities(),
      checkAvailableKarmaPoints(),
      // Leaderboard is often less critical for initial load, can be fetched separately or included
      // For now, keeping leaderboard fetch separate to not block primary loading state
    ]);
    setLastRefreshTime(new Date());
    setLoading(false); // Primary data loaded
  }, [user, fetchUserKarmaProfile, fetchKarmaActivities, checkAvailableKarmaPoints]);

  useEffect(() => {
    loadInitialData();
    fetchKarmaLeaderboard(); // Fetch leaderboard on initial load / user change too
  }, [user, loadInitialData, fetchKarmaLeaderboard]);
  
  // This combined fetch function can be used to refresh user-specific karma data
  const fetchAllUserKarmaData = useCallback(async () => {
    if (!user) return;
    setLoading(true); // Indicate refresh in progress
    await Promise.all([
      fetchUserKarmaProfile(),
      fetchKarmaActivities(),
      checkAvailableKarmaPoints(),
    ]);
    setLastRefreshTime(new Date());
    setLoading(false);
  }, [user, fetchUserKarmaProfile, fetchKarmaActivities, checkAvailableKarmaPoints]);


  // Consolidate main loading state
  useEffect(() => {
     // Overall loading is true if any of the critical initial fetches are loading
    setLoading(loadingProfile || loadingActivities || loadingAvailability);
  }, [loadingProfile, loadingActivities, loadingAvailability]);

  return {
    karmaPoints,
    setKarmaPoints,
    activities,
    setActivities,
    loading, // This is the combined loading state for user profile, activities, availability
    availablePoints,
    setAvailablePoints,
    leaderboard,
    loadingLeaderboard, // Specific to leaderboard
    currentLevel,
    setCurrentLevel,
    fetchLeaderboard: fetchKarmaLeaderboard, // Exported as fetchLeaderboard
    fetchKarmaPoints: fetchAllUserKarmaData, // Renamed for clarity internally, exported as fetchKarmaPoints
    checkAvailablePoints: checkAvailableKarmaPoints, // Exported as checkAvailablePoints
  };
};
